"use client";

import { useCallback, useEffect, useId, useRef, useState } from "react";

import { HelpCircle } from "lucide-react";
import { useRouter } from "next/navigation";

import { Button } from "@/components/_ui/button";
import { Input } from "@/components/_ui/input";
import { Label } from "@/components/_ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/_ui/tooltip";
import { isValidImb, normalizeImb } from "@/utils/imb";
import { mapPublicTrackingError } from "@/utils/publicTracking/api";
import { usePublicTracking } from "@/utils/publicTracking/usePublicTracking";

interface PublicTrackingLookupProps {
  /** Prefill the IMb field (e.g. from a `?imb=` deep link / QR code). */
  initialImb?: string;
  /** When true and `initialImb` is a valid IMb, run the lookup once on mount. */
  autoLookup?: boolean;
  /** Fired after a successful lookup. Used by the popover wrapper to close. */
  onSubmitSuccess?: () => void;
}

/**
 * Heading + IMb input form. Owns the input state, fires the lookup via
 * SWR mutation, and syncs the IMb to the URL on success (so the page's
 * server component can detect dashboard mode). No Card/Popover chrome —
 * callers wrap this for their context.
 */
export function PublicTrackingLookup({
  initialImb,
  autoLookup = false,
  onSubmitSuccess,
}: PublicTrackingLookupProps) {
  const router = useRouter();
  const inputId = useId();
  const [imb, setImb] = useState(() => initialImb ?? "");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const { trigger, isMutating } = usePublicTracking();
  const autoLookupFired = useRef(false);

  const runLookup = useCallback(
    async (value: string) => {
      setErrorMsg(null);
      try {
        await trigger(value);
        // Sync the IMb to the URL so the page (server component) knows we're
        // on the dashboard and can show the "New IMb" header link. Replace,
        // not push — back goes to wherever the user came from, not through
        // the in-flight state.
        router.replace(`/track?imb=${encodeURIComponent(value)}`);
        onSubmitSuccess?.();
      } catch (err) {
        setErrorMsg(mapPublicTrackingError(err));
      }
    },
    [trigger, router, onSubmitSuccess],
  );

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValidImb(imb)) {
      setErrorMsg(
        "Please enter the 31-digit number printed below the barcode.",
      );
      return;
    }
    void runLookup(normalizeImb(imb));
  }

  // One-shot auto-lookup on mount for a valid deep-linked IMb (?imb=...).
  useEffect(() => {
    if (autoLookupFired.current || !autoLookup) return;
    const candidate = initialImb ?? "";
    if (!isValidImb(candidate)) return;
    autoLookupFired.current = true;
    void runLookup(normalizeImb(candidate));
  }, [autoLookup, initialImb, runLookup]);

  const canSubmit = isValidImb(imb);
  // Bridge the render-before-useEffect gap on ?imb= deep links so the button
  // shows its loading state immediately rather than flashing enabled-then-loading.
  const autoLookupPending =
    autoLookup && !autoLookupFired.current && isValidImb(initialImb ?? "");
  const loading = isMutating || autoLookupPending;

  return (
    <div className="space-y-4">
      <div className="space-y-1.5 text-center">
        <div className="flex items-center justify-center gap-1.5">
          <h2 className="text-2xl font-semibold tracking-tight">
            Track your mail
          </h2>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="About this service"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <HelpCircle className="size-4" aria-hidden />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                Powered by USPS Informed Visibility
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <p className="text-muted-foreground text-sm">
          Enter your Intelligent Mail barcode to see its latest USPS scan
          history and expected delivery.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <div className="grid gap-2">
          <Label htmlFor={inputId}>Intelligent Mail barcode (IMb)</Label>
          <Input
            id={inputId}
            autoComplete="off"
            autoFocus
            spellCheck={false}
            inputMode="numeric"
            placeholder="e.g. 0014310442800127981514026100318"
            value={imb}
            onChange={(e) => setImb(e.target.value)}
            disabled={loading}
          />
          <p className="text-muted-foreground text-xs">
            Enter the 31-digit number printed beneath the barcode on your mail
            piece. Scans usually appear within a few hours of mailing.
          </p>
        </div>

        {errorMsg && <p className="text-destructive text-sm">{errorMsg}</p>}

        <div className="flex justify-end">
          <Button type="submit" disabled={!canSubmit} loading={loading}>
            Track
          </Button>
        </div>
      </form>
    </div>
  );
}
